import React from 'react';
import {
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';

export type CommonTableColumn<T> = {
  id: string;
  header: React.ReactNode;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  width?: string | number;
  padding?: 'normal' | 'checkbox' | 'none';
  hideRightBorder?: boolean;
  headerSx?: SxProps<Theme>;
  cellSx?: SxProps<Theme>;
  renderCell: (row: T) => React.ReactNode;
};

type CommonTableProps<T> = {
  columns: CommonTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  selectedRowIds?: string[];
  emptyState?: React.ReactNode;
  prependBody?: React.ReactNode;
  appendBody?: React.ReactNode;
  renderRow?: (row: T) => React.ReactNode;
  tableSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
};

export const CommonTable = <T,>({
  columns,
  rows,
  rowKey,
  selectedRowIds,
  emptyState,
  prependBody,
  appendBody,
  renderRow,
  tableSx,
  containerSx,
}: CommonTableProps<T>) => {
  return (
    <TableContainer sx={[{ overflowX: 'auto' }, ...(Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : [])]}>
      <Table
        sx={[{ minWidth: 600 }, ...(Array.isArray(tableSx) ? tableSx : tableSx ? [tableSx] : [])]}>
        <TableHead
          sx={(theme) => ({
            backgroundColor: theme.palette.extendedGrey?.lighter,
            height: '48px',
            borderTop: `1px solid ${theme.palette.divider}`,
          })}>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                padding={column.padding}
                sx={[
                  (theme) => ({
                    width: column.width,
                    borderRight:
                      column.id === columns[columns.length - 1]?.id || column.hideRightBorder
                        ? 'none'
                        : `1px solid ${theme.palette.divider}`,
                  }),
                  ...(Array.isArray(column.headerSx)
                    ? column.headerSx
                    : column.headerSx
                      ? [column.headerSx]
                      : []),
                ]}>
                {typeof column.header === 'string' ? (
                  <Typography
                    variant="h6"
                    sx={(theme) => ({
                      color: theme.palette.text.secondary,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'none',
                      textWrap: 'nowrap',
                    })}>
                    {column.header}
                  </Typography>
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {prependBody}
          {rows.map((row) => {
            if (renderRow) {
              return <React.Fragment key={rowKey(row)}>{renderRow(row)}</React.Fragment>;
            }

            const rowId = rowKey(row);
            return (
              <TableRow
                key={rowId}
                hover
                selected={Boolean(selectedRowIds?.includes(rowId))}
                sx={(theme) => ({
                  '& > td': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    height: '56px',
                  },
                })}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    padding={column.padding}
                    sx={[
                      (theme) => ({
                        borderRight:
                          column.id === columns[columns.length - 1]?.id || column.hideRightBorder
                            ? 'none'
                            : `1px solid ${theme.palette.divider}`,
                      }),
                      ...(Array.isArray(column.cellSx)
                        ? column.cellSx
                        : column.cellSx
                          ? [column.cellSx]
                          : []),
                    ]}>
                    {column.renderCell(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {!rows.length && !prependBody && emptyState}
          {appendBody}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
